import React, { useRef, useState, ReactNode } from "react";
import FlexBox from "../../../FlexBox/FlexBox";
import { ILandingInnovationProduct } from "../LandingInnovation";
import Typography from "../../../../Foundation/Typography/Typography";
import { Dot } from "../../../Dot/Dot";
import { ImageResponsive } from "../../../Image";
import { ImageFit } from "../../../Image/interface/ImageData";
import { _InnovationProductRoot } from "./InnovationProduct.styled";
import clsx from "clsx";
import Button from "../../../../Foundation/Button/Button";
import ShowMoreLink from "../../../../Navigation/ShowMoreLink/ShowMoreLink";
import { useHighlightedTypographyContext } from "../../../HighlightedTypography";
import useBreakpoint from "../../../Grid/hooks/useBreakpoint";
import { useTypographyContext } from "../../../../Foundation/Typography/Typography.context";

/** Context du visuel produit (ratio, variant, données produit…) pour un render prop. */
export interface InnovationProductVisualRenderContext<T = any> {
  /** Ratio largeur/hauteur — identique au fallback `ImageResponsive` */
  aspectRatio: number;
  variant: NonNullable<IInnovationProductProps<T>["variant"]>;
  product: ILandingInnovationProduct<T>;
  /** ID image catalogue utilisé en secours */
  imageId: string;
  selectiveView?: IInnovationProductProps<T>["selectiveView"];
  isMobileViewport: boolean;
  /**
   * Desktop chef + `renderCustomVisual` : conteneur commune visuel + overlay CTA —
   * à brancher sur `InnovationProductTileVisual.videoHoverPlayContainerRef` pour
   * que la vidéo reste en lecture au survol du bouton.
   */
  chefDesktopVideoHoverZoneRef?: React.RefObject<HTMLDivElement | null>;
}

export interface IInnovationProductProps<T extends any = any> {
  product: ILandingInnovationProduct<T>;
  className?: string;
  variant?: "default" | "text-on-desktop" | "chef-2024" | "mosaique";
  onClick?: (product: ILandingInnovationProduct<T>) => void;
  showLessLabel: string;
  showMoreLabel: string;
  ctaLabel?: string;
  hideCta?: boolean;
  selectiveView?: "image" | "text";
  /**
   * Remplace le bloc `ImageResponsive`. Préféré à un `ReactNode` statique pour accéder
   * au contexte (ratio, variant, etc.) au moment du rendu.
   */
  renderCustomVisual?: (
    ctx: InnovationProductVisualRenderContext<T>
  ) => ReactNode;
}

const InnovationProduct = <T extends any = any>(
  props: IInnovationProductProps<T>
) => {
  const {
    product,
    className,
    variant = "default",
    onClick,
    showLessLabel,
    showMoreLabel,
    ctaLabel: _ctaLabel,
    hideCta: hideCtaProp,
    selectiveView,
    renderCustomVisual,
  } = props;
  const imageId = product.imageId;
  const imageOnly = selectiveView === "image";
  const textOnlty = selectiveView === "text";
  const { mobile } = useBreakpoint();

  const isTextOnDesktop = variant === "text-on-desktop";
  const isChef2024 = variant === "chef-2024";
  const isMosaique = variant === "mosaique";

  const aspectRatio = variant === "chef-2024" ? 5 / 6 : 1;

  const useCustomVisualRenderer = typeof renderCustomVisual === "function";
  const chefDesktopCustomMediaZone =
    isChef2024 && !mobile && useCustomVisualRenderer;
  const chefHoverZoneRef = useRef<HTMLDivElement | null>(null);

  const customVisualNode = useCustomVisualRenderer
    ? renderCustomVisual({
        aspectRatio,
        variant,
        product,
        imageId: imageId ?? "",
        selectiveView,
        isMobileViewport: mobile,
        ...(chefDesktopCustomMediaZone
          ? { chefDesktopVideoHoverZoneRef: chefHoverZoneRef }
          : {}),
      })
    : undefined;

  const ctaLabel = _ctaLabel || product.ctaLabel || "Je commande";
  const hideCta =
    hideCtaProp === true ||
    Boolean((product as { hideCta?: boolean }).hideCta);
  const { highlightedColor: highlightedColorContext } =
    useHighlightedTypographyContext();
  const { grayTextColor } = useTypographyContext();

  const [showMore, setShowMore] = useState<boolean>(false);
  const [isClamped, setClamped] = useState(false);
  // const [canReduce, setCanReduce] = useState(false);

  const handleShowMore = () => {
    // setCanReduce(isClamped);
    setShowMore(!showMore);
  };
  return (
    <_InnovationProductRoot
      direction="column"
      gap={12}
      className={clsx(className, {
        "action-on-hover": isChef2024 && !mobile,
        mosaique: isMosaique,
      })}
      onClick={isMosaique ? () => onClick?.(product) : undefined}
    >
      {!textOnlty && (
        <div
          className={clsx("innovation-product", variant, {
            "action-on-hover": isChef2024,
          })}
        >
          {chefDesktopCustomMediaZone ? (
            <div
              ref={chefHoverZoneRef}
              className="innovation-product__chef-media-hover-zone"
              style={{ position: "relative", width: "100%" }}
            >
              {customVisualNode}
              {!hideCta && (
                <div className="action-on-hover--overlay">
                  <Button
                    variant="champagne"
                    className={clsx("innovation-product-action-button", variant)}
                    onClick={() => onClick?.(product)}
                  >
                    {ctaLabel}
                  </Button>
                </div>
              )}
            </div>
          ) : useCustomVisualRenderer ? (
            customVisualNode
          ) : (
            <ImageResponsive
              alt={product.title}
              desktop={{
                image: {
                  id: imageId,
                  fit: ImageFit.CONTAIN,
                  quality: 80,
                  aspectRatio,
                  width: 600,
                },
                wrapperStyle: {
                  maxWidth: "100%",
                  //   maxHeight: 327,
                  borderRadius: 0,
                  overflow: "hidden",
                  aspectRatio,
                },
                imgStyle: {
                  width: "100%",
                  aspectRatio,
                },
              }}
              mobile={{
                image: {
                  id: imageId,
                  fit: ImageFit.CONTAIN,
                  quality: 100,
                  aspectRatio,
                  outputPixelDensities: [1, 2],
                  width: 327,
                  //   height: 327,
                },
                wrapperStyle: {
                  maxWidth: "100%",
                  width: "100%",
                  //   maxHeight: 327,
                  borderRadius: 4,
                  aspectRatio,
                  overflow: "hidden",
                },
                imgStyle: {
                  width: "100%",
                  aspectRatio,
                },
              }}
              //   onClick={handleClickProduct}
              //   as={href ? "a" : "div"}
              //   {...(href && { href: href })}
            />
          )}
          {!chefDesktopCustomMediaZone &&
            isChef2024 &&
            !mobile &&
            !hideCta && (
              <div className="action-on-hover--overlay">
                <Button
                  variant="champagne"
                  className={clsx("innovation-product-action-button", variant)}
                  onClick={() => onClick?.(product)}
                >
                  {ctaLabel}
                </Button>
              </div>
            )}
        </div>
      )}
      {!imageOnly && (
        <FlexBox
          direction="column"
          gap={8}
          style={{
            height: "100%",
            flex: isMosaique ? 1 : undefined,
            width: isMosaique ? "100%" : undefined,
            ...(isChef2024 && {
              maxWidth: !mobile ? 280 : "100%",
              width: "100%",
            }),
          }}
        >
          <Typography
            variant={isChef2024 ? "h6" : "h7"}
            color={
              !isMosaique && product.color
                ? () => product.color || ""
                : undefined
            }
            style={
              isChef2024
                ? { textTransform: "uppercase" }
                : { fontWeight: "bold" }
            }
          >
            {product.title}
          </Typography>
          {!isMosaique && (
            <>
              <Typography
                variant="subtitle2"
                ellipse={!showMore}
                ellipseLine={3}
                showSeeMore={true}
                onClamped={setClamped}
              >
                {product.subtitle}
              </Typography>
              {isClamped && (
                <ShowMoreLink
                  // noIcon
                  icon={showMore ? "minus" : "plus"}
                  label={showMore ? showLessLabel : showMoreLabel}
                  color={highlightedColorContext}
                  onClick={handleShowMore}
                />
              )}
            </>
          )}
          {(isTextOnDesktop || isChef2024) && !isMosaique && (
            <Typography variant="body2">{product.description}</Typography>
          )}

          {((product.displayPrice && product.price) ||
            product.priceSubtitle) && (
            <FlexBox
              direction="column"
              style={{ marginTop: "auto" }}
              gap={4}
              wrap={"wrap"}
            >
              {product.displayFromMention && product.price && (
                <Typography
                  variant="ui4"
                  style={
                    isMosaique
                      ? undefined
                      : {
                          marginTop: 16,
                        }
                  }
                >
                  {product.displayFromMention}
                </Typography>
              )}
              {product.price || product.priceSubtitle ? (
                <FlexBox
                  style={{
                    height: 25,
                    ...(isMosaique ? {} : { marginTop: "auto" }),
                  }}
                  wrap={"wrap"}
                >
                  {product.price && (
                    <Typography
                      variant={isChef2024 ? "h7bold" : "subtitle2"}
                      useContextColor={true}
                      color="champagne7"
                    >
                      {product.price}
                    </Typography>
                  )}
                  {product.priceSubtitle && product.price && (
                    <Dot color={isChef2024 ? "greyText2" : "greyText"} />
                  )}
                  {product.priceSubtitle && (
                    <Typography
                      className="dot-separator"
                      variant="subtitle2"
                      color={
                        grayTextColor
                          ? () => grayTextColor
                          : isChef2024
                          ? "greyText2"
                          : "greyText"
                      }
                    >
                      {product.priceSubtitle}
                    </Typography>
                  )}
                </FlexBox>
              ) : null}
            </FlexBox>
          )}

          {(!isChef2024 || (isChef2024 && mobile)) && !hideCta && !isMosaique && (
            <div>
              <Button
                variant="champagne"
                className={clsx("innovation-product-action-button", variant)}
                onClick={() => onClick?.(product)}
              >
                {ctaLabel}
              </Button>
            </div>
          )}
        </FlexBox>
      )}
    </_InnovationProductRoot>
  );
};

export default InnovationProduct;
